/**
 * Hash-based SPA client router for Zuri Web App.
 * Handles view transitions, page cleanup, and route navigation.
 */

type PageRenderer = (container: HTMLElement) => void | (() => void) | Promise<void> | Promise<() => void>;

interface Route {
  path: string;
  render: PageRenderer;
}

export class Router {
  private routes: Route[] = [];
  private container: HTMLElement | null = null;

  register(path: string, render: PageRenderer): void {
    this.routes.push({ path, render });
  }

  mount(container: HTMLElement): void {
    this.container = container;
    window.addEventListener('hashchange', () => this.handleRoute());
    window.addEventListener('popstate', () => this.handleRoute());
    this.handleRoute();
  }

  async handleRoute(): Promise<void> {
    if (!this.container) return;

    // Run cleanup on previous page if attached
    const mountEl = this.container as HTMLElement & { _cleanup?: () => void };
    if (typeof mountEl._cleanup === 'function') {
      try {
        mountEl._cleanup();
      } catch { /* ignore cleanup errors */ }
      delete mountEl._cleanup;
    }

    // Scroll to top on page change
    window.scrollTo(0, 0);

    const hash = window.location.hash.replace('#', '') || '/';
    const route = this.routes.find(r => r.path === hash) 
      || this.routes.find(r => r.path === '404');

    if (route) {
      try {
        const cleanup = await route.render(this.container);
        if (typeof cleanup === 'function') {
          mountEl._cleanup = cleanup;
        }
      } catch (err) {
        console.error(`Failed to render route "${hash}":`, err);
        this.container.innerHTML = `
          <div class="empty-state" style="padding-top:120px">
            <i class="fa-solid fa-triangle-exclamation"></i>
            <h2>Page Render Error</h2>
            <p>An unexpected error occurred while rendering this page.</p>
          </div>
        `;
      }
    }
  }

  navigate(path: string): void {
    window.location.hash = path;
  }
}

export const router = new Router();
